import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { env } from '@/lib/env';
import type { PageMeta } from '@/types/api';

import { endpoints } from './endpoints';
import { ApiError, parseApiError, UnauthorizedError } from './errors';
import { sessionExpiry } from './session-expiry';
import { tokenStore } from './token-store';

type RetryConfig = AxiosRequestConfig & { _retried?: boolean };

/** Endpoints whose own 401 means "bad input"/"no session yet", not "stale session" —
 *  never refresh-retry these. Prefix matches cover login (incl. otp send/verify) and the
 *  whole forgot-password flow (send/verify/reset). First-login activation belongs here for
 *  the same reason and is the most literal case of "no session yet": the caller holds a
 *  single-purpose `passwordSetToken` and no refresh cookie exists, so a refresh-retry would
 *  fail and replace the endpoint's own `INVALID_TOKEN` with a code-less "Refresh failed",
 *  breaking the expired-token redirect (ADO 117536). */
const AUTH_ENTRY_PATHS = [
  '/auth/admin/login',
  '/auth/admin/forgot-password',
  endpoints.auth.activateSetPassword,
  endpoints.auth.refresh,
];

function isAuthEntry(url: string | undefined): boolean {
  return !!url && AUTH_ENTRY_PATHS.some((p) => url.includes(p));
}

/** A file download asks for `responseType: 'blob'`, and axios applies that to the **error** body too,
 *  so an ADR-0008 envelope arrives as a Blob that `parseApiError` cannot see into — the server's
 *  message was replaced by axios's "Request failed with status code 409" (ADO 120997). A body that
 *  is not JSON is handed back untouched. */
async function readErrorBody(data: unknown): Promise<unknown> {
  if (!(data instanceof Blob)) return data;
  try {
    return JSON.parse(await data.text()) as unknown;
  } catch {
    return data;
  }
}

export function createApiClient(baseURL: string): AxiosInstance {
  const instance = axios.create({ baseURL, withCredentials: true });
  let refreshInFlight: Promise<void> | null = null;

  instance.interceptors.request.use((config) => {
    const token = tokenStore.getAccessToken();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  });

  const doRefresh = async () => {
    if (!refreshInFlight) {
      refreshInFlight = axios
        .post<{ data: { accessToken: string } }>(`${baseURL}${endpoints.auth.refresh}`, null, {
          withCredentials: true,
        })
        .then((res) => {
          tokenStore.setAccessToken(res.data.data.accessToken);
        })
        .finally(() => {
          refreshInFlight = null;
        });
    }
    return refreshInFlight;
  };

  instance.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const original = error.config as RetryConfig | undefined;
      if (!error.response || !original) return Promise.reject(error);
      const { status } = error.response;
      const data = await readErrorBody(error.response.data);

      if (status === 401 && !original._retried && !isAuthEntry(original.url)) {
        original._retried = true;
        try {
          await doRefresh();
        } catch {
          // The refresh cookie is dead, so every subsequent request would 401 the same way.
          // Clearing the token is not enough: without the signal the app stays `authenticated`
          // forever, rendering a logged-in shell over a session the server has already ended.
          tokenStore.clear();
          // `data` is this original request's own 401 body — the one `ActiveUserGuard` already
          // re-validated the account against, so a deactivation shows up here (`ACCOUNT_DEACTIVATED`)
          // before the refresh attempt even runs. Forwarding it (ADO 118305) is what lets a
          // subscriber show "This account is deactivated..." instead of a generic expiry message.
          const parsed = parseApiError(status, data, 'Refresh failed');
          sessionExpiry.expire({ code: parsed.code, message: parsed.message });
          return Promise.reject(new UnauthorizedError(parsed.message, data));
        }
        return instance.request(original);
      }

      const parsed = parseApiError(status, data, error.message);
      if (status === 401) {
        const unauthorizedError = new UnauthorizedError(parsed.message, data);
        unauthorizedError.code = parsed.code;
        unauthorizedError.details = parsed.details;
        unauthorizedError.hasServerMessage = parsed.hasServerMessage;
        return Promise.reject(unauthorizedError);
      }
      return Promise.reject(
        new ApiError(parsed.message, status, {
          code: parsed.code,
          details: parsed.details,
          data,
          hasServerMessage: parsed.hasServerMessage,
        }),
      );
    },
  );

  return instance;
}

/** The deep success-side seam over an axios instance. Strips the transport wrapper
 *  (`res.data`) and the `{ data }` success envelope so no call site peels by hand;
 *  errors still propagate from the instance's interceptor as `ApiError`. Validation
 *  stays at the call site (feature schemas), so the verbs return raw `T`/`unknown`. */
export type ApiRequest = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) => Promise<T>;
  /** Fetch a binary payload (file download) as a `Blob` — bypasses the `{ data }`
   *  envelope unwrap since these endpoints return raw file bytes, not JSON. */
  getBlob: (url: string, config?: AxiosRequestConfig) => Promise<Blob>;
  /** A binary response **with** the name the server gave it. `getBlob` throws the headers away,
   *  which is right for an export the caller names itself and wrong for a file the server names
   *  (the admin invoice, ADO 117767 — slugged server-side from the order's customer). */
  getFile: (
    url: string,
    config?: AxiosRequestConfig,
  ) => Promise<{ blob: Blob; contentDisposition: string | null }>;
  /** POST-bodied file download (`POST .../export` with a JSON filter body) — same envelope
   *  bypass as `getBlob`, but the filters ride the body instead of the query string. */
  postBlob: (url: string, body?: unknown, config?: AxiosRequestConfig) => Promise<Blob>;
  /** `getFile`'s POST twin, for the Requests-hub tabs whose export carries its filters in a JSON
   *  body. Both halves of the export flow must read the header, or which verb an endpoint happens
   *  to use would decide whether the server gets to name its own file. */
  postFile: (
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig,
  ) => Promise<{ blob: Blob; contentDisposition: string | null }>;
  /** List endpoints keep the `{ data, meta }` envelope — `meta` carries pagination. */
  getList: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ) => Promise<{ data: T[]; meta: PageMeta }>;
  /** POST-bodied list endpoints (`POST .../query`): same `{ data, meta }` shape as `getList`,
   *  but the backend nests it one level deeper (`{ data: { items, meta } }`) since the filter
   *  body, not a query string, carries the request. */
  postList: <T = unknown>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig,
  ) => Promise<{ data: T[]; meta: PageMeta }>;
  post: <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  put: <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  patch: <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  /** Resolves to the response **body**, envelope unpeeled — a DELETE's only signal can sit in the
   *  root `message` (device-telemetry cancel: cancelled vs already delivered). Callers that ignore
   *  it keep `void`. */
  delete: <T = void>(url: string, config?: AxiosRequestConfig) => Promise<T>;
};

export function createApiRequest(instance: AxiosInstance): ApiRequest {
  return {
    get: async <T = unknown>(url: string, config?: AxiosRequestConfig) =>
      (await instance.get<{ data: T }>(url, config)).data.data,
    getBlob: async (url: string, config?: AxiosRequestConfig) =>
      (await instance.get<Blob>(url, { ...config, responseType: 'blob' })).data,
    getFile: async (url: string, config?: AxiosRequestConfig) => {
      const res = await instance.get<Blob>(url, { ...config, responseType: 'blob' });
      // Absent unless the API exposes it via CORS `exposedHeaders`; the caller supplies a fallback.
      const header = res.headers['content-disposition'] as string | undefined;
      return { blob: res.data, contentDisposition: header ?? null };
    },
    postBlob: async (url: string, body?: unknown, config?: AxiosRequestConfig) =>
      (await instance.post<Blob>(url, body, { ...config, responseType: 'blob' })).data,
    postFile: async (url: string, body?: unknown, config?: AxiosRequestConfig) => {
      const res = await instance.post<Blob>(url, body, { ...config, responseType: 'blob' });
      const header = res.headers['content-disposition'] as string | undefined;
      return { blob: res.data, contentDisposition: header ?? null };
    },
    getList: async <T = unknown>(url: string, config?: AxiosRequestConfig) =>
      (await instance.get<{ data: T[]; meta: PageMeta }>(url, config)).data,
    postList: async <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) => {
      const { items, meta } = (
        await instance.post<{ data: { items: T[]; meta: Omit<PageMeta, 'itemCount'> } }>(
          url,
          body,
          config,
        )
      ).data.data;
      return { data: items, meta: { ...meta, itemCount: items.length } };
    },
    post: async <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
      (await instance.post<{ data: T }>(url, body, config)).data.data,
    put: async <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
      (await instance.put<{ data: T }>(url, body, config)).data.data,
    patch: async <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
      (await instance.patch<{ data: T }>(url, body, config)).data.data,
    delete: async <T = void>(url: string, config?: AxiosRequestConfig) =>
      (await instance.delete<T>(url, config)).data,
  };
}

export const apiClient = createApiRequest(createApiClient(env.VITE_API_URL));
